import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import {
  AlertController,
  LoadingController,
  ModalController,
  PopoverController,
  PopoverOptions,
} from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import {
  combineLatest,
  fromEvent,
  Observable,
  Subject,
  Subscription,
} from "rxjs";
import { takeUntil } from "rxjs/operators";
import { globalHelper } from "src/app/_helpers/global";
import { Utils } from "src/app/_helpers/utils";
import { GetSpacesModel } from "src/app/_models/space-requests.model";
import { SpaceModel } from "src/app/_models/space.model";
import { ThisPlatform } from "src/app/_services/platform.service";
import { SpaceService } from "src/app/_services/space.service";
import { SpaceType } from "src/app/_types/space-type";
import { PopoverButton, PopoverLegend } from "../../popover/popover.model";
import { SpaceFilter } from "../space-filter/space-filter.model";
import { SpaceFilterService } from "../space-filter/space-filter.service";
import { SpaceMapAssignSpaceComponent } from "../space-map-edit/space-map-assign-space/space-map-assign-space.component";
import { UserAssignmentViewDetailsComponent } from "../user-assignment-view/user-assignment-view-details/user-assignment-view-details.component";
import { MapWorkingMode } from "./space-map.model";

@Component({
  selector: "app-space-map",
  templateUrl: "./space-map.component.html",
  styleUrls: ["./space-map.component.scss"],
})
export class SpaceMapComponent implements OnInit {
  constructor(
    public p: ThisPlatform,
    private popoverController: PopoverController,
    private spaceFilterService: SpaceFilterService,
    private modalCtrl: ModalController,
    public spaceService: SpaceService,
    private translateService: TranslateService,
    private alertController: AlertController,
    private loadingCtrl: LoadingController
  ) {}

  spaceType: typeof SpaceType = SpaceType;

  buttonListEditMode: PopoverButton[] = [
    {
      name: this.translateService.instant("SPACE_MAP.ASSIGN_SPACE"),
      icon: "car",
      eventType: "assign",
    },
    {
      name: this.translateService.instant("DETAILS"),
      icon: "information-circle",
      eventType: "info",
    },
    {
      name: this.translateService.instant("DELETE"),
      icon: "trash-outline",
      eventType: "remove",
    },
  ];

  buttonListBookMode: PopoverButton[] = [
    {
      name: this.translateService.instant("SPACE.BOOK_PLACE"),
      icon: "checkmark",
      eventType: "book",
    },
    {
      name: this.translateService.instant("DETAILS"),
      icon: "information-circle",
      eventType: "info",
    },
  ];

  buttonListViewMode: PopoverButton[] = [
    {
      name: this.translateService.instant("DETAILS"),
      icon: "information-circle",
      eventType: "info",
    },
  ];

  buttonList: PopoverButton[];

  temporarySpaceList = [];

  mapWorkingMode: typeof MapWorkingMode = MapWorkingMode;

  @Input() workingMode: MapWorkingMode;

  @Input() enableMovingInput: Observable<boolean>;

  @Input() freeSpacesLoaded: Observable<SpaceModel[]>;

  @Input() resetSpaces: Observable<void>;

  @Output()
  enableMovingSpacesButton: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  onBookSpace: EventEmitter<SpaceModel> = new EventEmitter<SpaceModel>();

  @ViewChild("mapContainer", { static: true })
  mapContainer: ElementRef<HTMLElement>;

  @ViewChild("map", { static: true })
  map: ElementRef<HTMLElement>;

  @ViewChild("mapImage", { static: true })
  mapImage: ElementRef<HTMLElement>;

  spaceList: SpaceModel[] = [];

  backgroundImage: string = "";

  noResultInfoTranslationKey: string = "";

  eventsSubject: Subject<void> = new Subject<void>();

  spacesAndBookings: any;

  moveBackgroundSubscription: Subscription;
  moveSpaceSubscription: Subscription;

  backgroundPositionX: number = 0;
  backgroundPositionY: number = 60;

  startOffsetX: number = 0;
  startOffsetY: number = 60;

  mapWidth: number;
  mapHeight: number;

  touchMoveDirectionX: number;
  touchMoveDirectionY: number;

  spaceMoveStartPositionX: number;
  spaceMoveStartPositionY: number;

  zoom: number = 1;

  enableMovingSpace: boolean = false;

  backgroundMoving: boolean = false;
  spaceMoving: boolean = false;

  limitMovement: boolean = false;

  showScrollAlert: boolean = false;

  buildingId: number;
  floorId: number;

  popoverLegendEditMode: PopoverLegend[] = [
    {
      color: "#008000",
      label: "SPACE.ASSIGNED_SPACE",
      character: "1",
    },
    {
      color: "#ffc409",
      label: "SPACE.UNASIGNED_SPACE",
      character: "?",
    },
  ];

  popoverLegendBookingMode: PopoverLegend[] = [
    {
      color: "#008000",
      label: "SPACE.FREE_SPACE",
      character: "1",
    },
    {
      color: "#d60d8c",
      label: "SPACE.OCCUPIED_SPACE",
      character: "X",
    },
  ];

  popoverLegend: PopoverLegend[];

  destroy$: Subject<boolean> = new Subject<boolean>();
  destroySpaceFilter$: Subject<boolean> = new Subject<boolean>();

  resetSpacesSubscription: Subscription;
  enableMovingSubscription: Subscription;

  ngOnInit(): void {
    if (this.workingMode === MapWorkingMode.EDIT) {
      this.enableMovingSubscription = this.enableMovingInput.subscribe(
        (enable) => {
          this.enableMovingSpace = enable;
        }
      );
      this.popoverLegend = this.popoverLegendEditMode;
    }

    if (this.workingMode === MapWorkingMode.BOOK) {
      this.popoverLegend = this.popoverLegendBookingMode;
    }

    let obs = new ResizeObserver(() => {
      this.mapWidth = this.mapImage.nativeElement.offsetWidth;
      this.mapHeight = this.mapImage.nativeElement.offsetHeight;
    });
    obs.observe(this.mapImage.nativeElement);

    // poniższy warunek naprawia czasem występujący błąd wywalający mapki.
    if (this.spaceFilterService.filterDetails === null) {
      this.spaceFilterService.filterDetails = new SpaceFilter();
    }

    this.loadMapDetails(this.spaceFilterService.filterDetails);

    this.spaceFilterService.filterDetailsSubject
      .pipe(takeUntil(this.destroySpaceFilter$))
      .subscribe((filterDetails: SpaceFilter) => {
        this.loadMapDetails(filterDetails);
      });

    this.resetSpacesSubscription = this.resetSpaces.subscribe(() => {
      this.spaceList.forEach((space) => {
        space.free = false;
      });
    });
  }

  loadMapDetails(filterDetails: SpaceFilter) {
    if (
      filterDetails?.floorList.length === 1 &&
      filterDetails?.floorList[0].floorPlan
    ) {
      this.backgroundImage = filterDetails.floorList[0].floorPlan.filePath;
      this.buildingId = filterDetails.buildingListId[0];
      this.floorId = filterDetails.floorListId[0];
      this.mapWidth = this.mapImage.nativeElement.offsetWidth;
      this.mapHeight = this.mapImage.nativeElement.offsetHeight;
      if (this.workingMode === MapWorkingMode.EDIT) {
        this.loadSpaces();
        this.enableMovingSpacesButton.next(true);
      }
      if (this.workingMode === MapWorkingMode.BOOK) {
        this.loadSpacesAndBookings();
      }
    } else {
      this.backgroundImage = "";
      this.mapWidth = this.mapImage.nativeElement.offsetWidth;
      this.mapHeight = this.mapImage.nativeElement.offsetHeight;
      if (this.workingMode === MapWorkingMode.EDIT) {
        this.enableMovingSpacesButton.next(false);
      }
    }

    if (filterDetails.floorList.length === 0) {
      this.noResultInfoTranslationKey = "SPACE_MAP.SELECT_BUILDING_AND_FLOOR";
    } else if (filterDetails.floorList.length > 1) {
      this.noResultInfoTranslationKey =
        "SPACE_MAP.SELECT_ONE_BUILDING_AND_FLOOR";
    } else if (filterDetails.floorList.length === 1 && !this.backgroundImage) {
      this.noResultInfoTranslationKey = "SPACE_MAP.PLAN_MISSING";
    }
  }

  loadSpaces() {
    let requestParams: GetSpacesModel = {
      type: SpaceType.Parking,
      buildings: this.buildingId.toString(),
      floors: this.floorId.toString(),
    };

    this.spaceService
      .getSpaces(requestParams)
      .subscribe((spaces: SpaceModel[]) => {
        this.spaceList = spaces;
      });
  }

  loadSpacesAndBookings() {
    let requestParams: GetSpacesModel = {
      type: SpaceType.Parking,
      buildings: this.buildingId.toString(),
      floors: this.floorId.toString(),
    };

    combineLatest(
      this.spaceService.getSpaces(requestParams),
      this.freeSpacesLoaded
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((resp) => {
        let [allSpaces, freeSpaces] = resp;
        allSpaces.forEach((space: SpaceModel) => {
          if (this.spaceService.spaces.length > 0) {
            this.spaceService.spaces.forEach((freeSpace: SpaceModel) => {
              if (space.id === freeSpace.id) {
                space.free = true;
              }
            });
          }
        });
        this.spaceList = allSpaces;
      });
  }

  onMoveBackground(event: any, moveType) {
    if (!this.enableMovingSpace) {
      this.backgroundMoving = true;

      let startOffsetX;
      let startOffsetY;

      if (event.type === "mousedown") {
        startOffsetX = -this.backgroundPositionX + event.clientX;
        startOffsetY = -this.backgroundPositionY + event.clientY;
      }
      if (event.type === "touchstart") {
        startOffsetX = -this.backgroundPositionX + event.touches[0].clientX;
        startOffsetY = -this.backgroundPositionY + event.touches[0].clientY;

        document
          .querySelector(".space-add-reservation__container")
          .classList.add("space-add-reservation__container--block-scrolling");
      }

      this.moveBackgroundSubscription = fromEvent(
        this.mapContainer.nativeElement,
        moveType
      ).subscribe((e: any) => {
        if (this.backgroundMoving) {
          if (e.type === "touchmove") {
            this.backgroundPositionX = -startOffsetX + e.touches[0].clientX;
            this.backgroundPositionY = -startOffsetY + e.touches[0].clientY;
          }
          if (e.type === "mousemove") {
            this.backgroundPositionX = -startOffsetX + e.clientX;
            this.backgroundPositionY = -startOffsetY + e.clientY;
          }
          this.limitMoveOnXAxis(e);
          this.limitMoveOnYAxis(e);
        }
      });
    }
  }

  moveEnd() {
    this.moveBackgroundSubscription.unsubscribe();
    this.backgroundMoving = false;
    document
      .querySelector(".space-add-reservation__container")
      .classList.remove("space-add-reservation__container--block-scrolling");
  }

  onToggleFilter() {
    this.eventsSubject.next();
  }

  async showInfo(space) {
    const modal = await this.modalCtrl.create({
      component: UserAssignmentViewDetailsComponent,
      animated: true,
      cssClass: "add-edit-modal add-edit-modal--small",
      componentProps: {
        row: space,
        type: "space",
      },
    });
    modal.present();
  }

  onSpaceToMove(event, space) {
    if (this.enableMovingSpace) {
      let HTMLSpaceToMove = event.currentTarget;
      this.spaceMoving = true;

      let startX = space.posX;
      let startY = space.posY;

      if (this.enableMovingSpace) {
        this.moveSpaceSubscription = fromEvent(
          this.map.nativeElement,
          "mousemove"
        ).subscribe((e: any) => {
          space.posX = startX - (event.screenX - e.screenX) * (1 / this.zoom);

          HTMLSpaceToMove.style.left = space.posX + "px";

          space.posY = startY - (event.screenY - e.screenY) * (1 / this.zoom);

          HTMLSpaceToMove.style.top = space.posY + "px";
        });
      }
    }
  }

  onDropSpace() {
    if (this.spaceMoving == true) {
      this.spaceMoving = false;
      this.moveSpaceSubscription.unsubscribe();
    }
  }

  onGetSpacePosition(spaceToSave: SpaceModel) {
    if (this.enableMovingSpace) {
      this.spaceMoveStartPositionX = spaceToSave.posX;
      this.spaceMoveStartPositionY = spaceToSave.posY;
    }
  }

  async onDropSpaceAndSave(spaceToUpdate: SpaceModel) {
    if (this.spaceMoving == true) {
      let confirm = await globalHelper.Alert(
        this.alertController,
        this.translateService,
        this.translateService.instant("SPACE_MAP.PLACE_MOVE_CONFIRMATION")
      );
      if (confirm) {
        this.loadingCtrl
          .create({
            keyboardClose: true,
            message: this.translateService.instant("SAVING"),
          })
          .then((loadingEl) => {
            loadingEl.present();

            this.spaceService.space.id = spaceToUpdate.id;

            this.spaceService.updateSpace(spaceToUpdate).subscribe(() => {
              this.loadSpaces();
              loadingEl.dismiss();
            });
          });
      } else {
        this.spaceList.forEach((space: SpaceModel) => {
          if (space.id === spaceToUpdate.id) {
            space.posX = this.spaceMoveStartPositionX;
            space.posY = this.spaceMoveStartPositionY;
          }
        });
      }

      this.spaceMoving = false;
      this.moveSpaceSubscription.unsubscribe();
    }
  }

  limitMoveOnXAxis(e) {
    let moveX;

    if (this.p.isMobile) {
      moveX = e.targetTouches[0].screenX - this.touchMoveDirectionX;
      this.touchMoveDirectionX = e.targetTouches[0].screenX;
    } else {
      moveX = e.movementX;
    }

    if (
      ((this.backgroundPositionX >
        (this.mapContainer.nativeElement.offsetWidth +
          (this.zoom - 1) * (this.mapWidth / 2)) *
          0.85 &&
        +moveX > 0) ||
        (-this.backgroundPositionX >
          (this.mapWidth + (this.zoom - 1) * (this.mapWidth / 2)) * 0.85 &&
          +moveX < 0)) &&
      !this.limitMovement
    ) {
      this.limitMovement = true;
      this.moveEnd();
    } else {
      this.limitMovement = false;
    }
  }

  limitMoveOnYAxis(e) {
    let moveY;

    if (this.p.isMobile) {
      moveY = e.targetTouches[0].screenY - this.touchMoveDirectionY;
      this.touchMoveDirectionY = e.targetTouches[0].screenY;
    } else {
      moveY = e.movementY;
    }

    if (
      ((this.backgroundPositionY >
        (this.mapContainer.nativeElement.offsetHeight +
          (this.zoom - 1) * (this.mapHeight / 2)) *
          0.8 &&
        moveY > 0) ||
        (-this.backgroundPositionY >
          (this.mapHeight + (this.zoom - 1) * (this.mapHeight / 2)) * 0.8 &&
          moveY < 0)) &&
      !this.limitMovement
    ) {
      this.limitMovement = true;
      this.moveEnd();
    } else {
      this.limitMovement = false;
    }
  }

  spaceClass(space) {
    if (this.workingMode === MapWorkingMode.EDIT) {
      if (space.posX && space.posY) {
        return "space-map__space--assigned";
      } else {
        return "space-map__space--free";
      }
    }

    if (this.workingMode === MapWorkingMode.BOOK) {
      if (space.free) {
        return "space-map__space--free";
      } else {
        return "space-map__space--occupied";
      }
    }
  }

  onWheel(event) {
    if (!this.enableMovingSpace && !event.shiftKey && !this.p.renderMobile) {
      this.showScrollAlert = true;
      setTimeout(() => {
        this.showScrollAlert = false;
      }, 500);
    }

    if (!this.enableMovingSpace && event.shiftKey) {
      if (event.deltaY < 0) {
        this.zoom += 0.1;
      } else {
        if (this.zoom > 0.2) {
          this.zoom -= 0.1;
        }
      }
    }
  }

  onButtonZoom(zoom) {
    this.zoom += zoom;
  }

  async onShowPopover(event: Event, space: SpaceModel) {
    if (!this.enableMovingSpace) {
      const popover = await globalHelper.Popover(
        this.popoverController,
        event,
        this.setButtonList(space),
        "user-assignment-view-share__popover",
        space.name !== undefined
          ? this.translateService.instant("SPACE.PLACE") + ": " + space.name
          : this.translateService.instant("SPACE.UNASIGNED_SPACE")
      );
      if (popover) {
        if (popover.data === "assign") {
          this.openSpaceListModal(space);
        }
        if (popover.data === "remove") {
          this.removePlace(space);
        }
        if (popover.data === "info") {
          this.showInfo(space);
        }
        if (popover.data === "book") {
          this.onBookSpace.next(space);
        }
      }
    }
  }

  setButtonList(space: SpaceModel): PopoverButton[] {
    if (this.workingMode === MapWorkingMode.EDIT) {
      return (this.buttonList = this.buttonListEditMode);
    }
    if (this.workingMode === MapWorkingMode.BOOK) {
      if (space?.free) {
        return (this.buttonList = this.buttonListBookMode);
      } else {
        return (this.buttonList = this.buttonListViewMode);
      }
    }
  }

  async onCreateNewPlace(event: MouseEvent) {
    if (this.workingMode === MapWorkingMode.EDIT) {
      this.temporarySpaceList.push({
        posX: event.offsetX,
        posY: event.offsetY,
      });
    }
  }

  async openSpaceListModal(space) {
    const modal = await this.modalCtrl.create({
      component: SpaceMapAssignSpaceComponent,
      animated: true,
      cssClass: "add-edit-modal",
      componentProps: {
        selectedSpace: space,
        spaceList: this.spaceList,
      },
    });
    modal.present();
    modal.onDidDismiss().then((data) => {
      if (data.role === "assignSpace") {
        this.assignPlaceToSpace(data.data, space, space.posX, space.posY);
      }
    });
  }

  assignPlaceToSpace(spaceToAssign, spaceToRemove, coordinateX, coordinateY) {
    let space: SpaceModel = spaceToAssign;
    space.posX = coordinateX;
    space.posY = coordinateY;

    if (spaceToRemove.id) {
      spaceToRemove.posX = null;
      spaceToRemove.posY = null;

      this.spaceService.space.id = spaceToRemove.id;
      this.spaceService.updateSpace(spaceToRemove).subscribe(() => {
        this.spaceService.space.id = spaceToAssign.id;
        this.spaceService.updateSpace(spaceToAssign).subscribe(() => {
          this.loadSpaces();
        });
      });
    } else {
      this.temporarySpaceList = this.temporarySpaceList.filter((tempSpace) => {
        return (
          tempSpace.posX !== spaceToRemove.posX &&
          tempSpace.posY !== spaceToRemove.posY
        );
      });
      this.spaceService.space.id = spaceToAssign.id;
      this.spaceService.updateSpace(spaceToAssign).subscribe(() => {
        this.loadSpaces();
      });
    }
  }

  async removePlace(spaceToRemove: SpaceModel) {
    let confirm = await globalHelper.Alert(
      this.alertController,
      this.translateService
    );
    if (confirm) {
      this.loadingCtrl
        .create({
          keyboardClose: true,
          message: this.translateService.instant("DELETING"),
        })
        .then((loadingEl) => {
          loadingEl.present();

          if (spaceToRemove.id) {
            spaceToRemove.posX = null;
            spaceToRemove.posY = null;

            this.spaceService.space.id = spaceToRemove.id;

            this.spaceService.updateSpace(spaceToRemove).subscribe(() => {
              this.loadSpaces();
              loadingEl.dismiss();
            });
          } else {
            this.temporarySpaceList = this.temporarySpaceList.filter(
              (tempSpace) => {
                return (
                  tempSpace.posX !== spaceToRemove.posX &&
                  tempSpace.posY !== spaceToRemove.posY
                );
              }
            );
            loadingEl.dismiss();
          }
        });
    }
  }

  removeTemporarySpace(coordinateX, coordinateY) {
    this.temporarySpaceList = this.temporarySpaceList.filter(
      (temporarySpace) =>
        temporarySpace.posX !== coordinateX &&
        temporarySpace.posY !== coordinateY
    );
  }

  async onShowInfo(event: Event) {
    const popover: PopoverOptions = await globalHelper.Popover(
      this.popoverController,
      event,
      [],
      "user-assignment-view-share__popover",
      "SPACE.LEGEND_HEADER",
      this.popoverLegend
    );
  }

  ngOnDestroy() {
    this.destroySpaceFilter$.next(true);
    this.destroySpaceFilter$.unsubscribe();

    if (this.workingMode === MapWorkingMode.EDIT) {
      this.enableMovingSubscription.unsubscribe();
    }
    if (this.workingMode === MapWorkingMode.BOOK) {
      this.resetSpacesSubscription.unsubscribe();
    }
    if (
      this.spaceFilterService.filterDetails.floorList.length === 1 &&
      this.spaceFilterService.filterDetails.floorList[0].floorPlan &&
      this.workingMode === MapWorkingMode.BOOK
    ) {
      this.destroy$.next(true);
      this.destroy$.unsubscribe();
    }
  }
}
